/**
 * CANONICAL PUBLIC MARKETING LOCALE CONFIG.
 *
 * Phase 1 scope: the marketing HOMEPAGE pair only.
 *   en -> /       (x-default)
 *   es -> /es/
 *
 * The AuthorityLayer application (auth, dashboard, scans, reports, billing,
 * admin, emails, AI generation) is English-only and MUST NOT read this module.
 * Nothing here performs browser-language or IP based redirection — language is
 * always an explicit user choice through real URLs.
 */

export type Locale = "en" | "es";

export const DEFAULT_LOCALE: Locale = "en";

export interface LocaleConfig {
  code: Locale;
  /** Native language name, used by the public language selector. */
  label: string;
  /** Compact selector label. */
  short: string;
  /** <html lang> value. */
  htmlLang: string;
  /** og:locale value. */
  ogLocale: string;
  /** Homepage route path for this locale, exactly as declared in App.tsx. */
  homePath: string;
  /** Canonical homepage URL path (locale roots are canonicalised with a trailing slash). */
  homeCanonicalPath: string;
}

export const LOCALES: Record<Locale, LocaleConfig> = {
  en: {
    code: "en",
    label: "English",
    short: "EN",
    htmlLang: "en",
    ogLocale: "en_US",
    homePath: "/",
    homeCanonicalPath: "/",
  },
  es: {
    code: "es",
    label: "Español",
    short: "ES",
    htmlLang: "es",
    ogLocale: "es_ES",
    homePath: "/es",
    homeCanonicalPath: "/es/",
  },
};

export const LOCALE_ORDER: Locale[] = ["en", "es"];

/** Router paths that are locale roots (canonicalised with a trailing slash). */
export const LOCALE_ROOT_PATHS: Record<string, string> = {
  "/es": "/es/",
};

export interface LanguageAlternate {
  hreflang: string;
  /** Canonical path, resolved to an absolute URL by the caller. */
  path: string;
}

/**
 * Reciprocal hreflang set for the homepage pair. Identical on both documents,
 * derived here once so the two sides can never drift.
 */
export const HOME_ALTERNATES: LanguageAlternate[] = [
  { hreflang: "en", path: LOCALES.en.homeCanonicalPath },
  { hreflang: "es", path: LOCALES.es.homeCanonicalPath },
  { hreflang: "x-default", path: LOCALES.en.homeCanonicalPath },
];

/** Reciprocal hreflang set for the pricing pair (/pricing, /es/pricing). */
export const PRICING_ALTERNATES: LanguageAlternate[] = [
  { hreflang: "en", path: "/pricing" },
  { hreflang: "es", path: "/es/pricing" },
  { hreflang: "x-default", path: "/pricing" },
];

/** Locale of a public marketing path: anything under /es is Spanish. */
export function marketingLocaleForPath(pathname: string): Locale {
  return pathname === "/es" || pathname.startsWith("/es/") ? "es" : "en";
}
